/**
 * AI Recruit - Live Interview API Service
 * Interviewer-facing (authenticated) session creation + TURN/STUN credentials.
 * Mirrors proctoredInterviewApi.ts's shape/conventions — this frontend does
 * NOT auto-convert casing anywhere in its API layer (see api.ts/jobsApi etc.),
 * so every field below matches the wire format exactly: snake_case in and
 * snake_case out, no camelCase transformation layer.
 *
 * Every LiveInterviewSession is scoped to a specific candidate submission
 * (job_match), never just a job — job_id is still returned (denormalized,
 * matching ProctoredInterviewSession's shape) but is always derived
 * server-side from the job_match, never trusted from the request body.
 */

import { apiV2 } from './api'

export interface LiveInterviewSession {
  id: string
  job_id: string
  job_match_id: string
  recruiter_seat_count: number
  status: string
  candidate_join_path: string
  // When the candidate's interview invite email was last sent — null until
  // the first successful send. Present on every session response (create,
  // get-or-create, listSessionsForJob and sendCandidateEmail below), same
  // "last sent" semantics as ProctoredInterviewApi's SubmissionSession
  // .invite_email_sent_at.
  invite_email_sent_at?: string | null
  // "human" (a recruiter conducts the call, as today) or "ai" (the AI
  // interviewer conducts the whole call — see LiveInterviewTab's Human/AI
  // toggle on the Send Email action).
  interview_mode?: 'human' | 'ai'
}

export interface IceServer {
  urls: string
  username?: string
  credential?: string
}

// Creates a brand-new session for this job_match — use getOrCreateSession
// instead for a recruiter-facing "get me a link" action, since re-clicking
// this would create a duplicate session for the same candidate.
export const createSession = async (
  jobId: string,
  jobMatchId: string,
  recruiterSeatCount: number = 1
): Promise<LiveInterviewSession> => {
  // job_id isn't part of the request body — the backend derives it from the
  // job_match itself (and would reject/ignore a mismatched one), but the
  // caller still needs it up front to know which job_match to point at.
  void jobId
  const response = await apiV2.post('/live-interview/sessions', {
    job_match_id: jobMatchId,
    recruiter_seat_count: recruiterSeatCount,
  })
  return response.data
}

// Idempotent: returns the existing session for this job_match if one
// already exists, otherwise creates it. This is what the recruiter-facing
// "Create / Get Interview Link" action should call — safe to call again for
// the same candidate without spawning a second session.
export const getOrCreateSession = async (
  jobMatchId: string,
  jobId: string,
  recruiterSeatCount: number = 1,
  interviewMode?: 'human' | 'ai'
): Promise<LiveInterviewSession> => {
  void jobId
  const response = await apiV2.post(`/live-interview/job-matches/${jobMatchId}/sessions/get-or-create`, {
    job_match_id: jobMatchId,
    recruiter_seat_count: recruiterSeatCount,
    interview_mode: interviewMode,
  })
  return response.data
}

// Every job_match_id in this job that has a (non-aborted) session, keyed by
// job_match_id — the bulk counterpart to getOrCreateSession, used to show the
// Candidates list's real Join Now/Completed status for every row up front
// instead of learning it lazily, one row at a time, as each is clicked.
export const listSessionsForJob = async (jobId: string): Promise<Record<string, LiveInterviewSession>> => {
  const response = await apiV2.get(`/live-interview/jobs/${jobId}/sessions`)
  return response.data
}

export const getTurnCredentials = async (): Promise<IceServer[]> => {
  const response = await apiV2.get('/live-interview/turn-credentials')
  return response.data
}

export interface SpeechToken {
  token: string
  // The real-time recognition WebSocket is a region-only capability —
  // confirmed via a live test that connecting via this resource's custom-
  // subdomain endpoint instead fails outright (WS close code 1006). See
  // useLiveTranscription.ts (SpeechConfig.fromAuthorizationToken(token, region)).
  region: string
  expires_in_seconds: number
  // Reconnect/refresh tuning, all server-configured (core/config.py's
  // LIVE_TRANSCRIPTION_* settings) — the hook reads these instead of
  // hardcoding its own timing, so an hours-long-interview tuning change is
  // a .env edit, not a frontend redeploy.
  token_refresh_seconds: number
  reconnect_burst_base_delay_ms: number
  reconnect_burst_max_attempts: number
  long_retry_interval_seconds: number
  // AI interviewer voice (Azure Neural TTS) — server-configured
  // (AI_INTERVIEWER_TTS_*), see hooks/useAiInterviewerVoice.ts.
  tts_voice?: string
  tts_style?: string
  tts_rate?: string
}

// Short-lived Azure AI Speech token for the recruiter room's live
// transcription — see hooks/useLiveTranscription.ts. Minted fresh on every
// call; the hook re-fetches on its own refresh schedule (server-configured),
// never caches this beyond one recognizer session.
export const getSpeechToken = async (): Promise<SpeechToken> => {
  const response = await apiV2.get('/live-interview/speech-token')
  return response.data
}

export interface SendInviteEmailResponse {
  sent: boolean
  message: string
  sent_at?: string | null
  // The (possibly just-created) session — a Live Interview session, unlike
  // Proctored Interview's, doesn't necessarily exist yet when this is
  // called, so the backend get-or-creates it and hands it back here. Merge
  // this into the tab's local `sessions` map so the row's status/buttons are
  // correct immediately.
  session: LiveInterviewSession
}

// Emails the candidate their Live Interview join link — the Live Interview
// twin of proctoredInterviewApi.ts's sendCandidateEmail. Addressed by
// job_match_id rather than session_id (unlike Proctored Interview) because a
// Live Interview session is only ever created on demand: this get-or-creates
// it server-side first, the same way getOrCreateSession above does.
export const sendCandidateEmail = async (
  jobMatchId: string, interviewMode: 'human' | 'ai' = 'human'
): Promise<SendInviteEmailResponse> => {
  const response = await apiV2.post(
    `/live-interview/job-matches/${jobMatchId}/sessions/send-email`, null, { params: { interview_mode: interviewMode } }
  )
  return response.data
}

// Interim (Phase A) summary shapes — mirrors the backend's
// LiveInterviewSummaryResponse in api/v2/endpoints/live_interview.py.
// `participants` is always empty for now (no code path creates
// LiveInterviewParticipant rows yet); `recording` is null until a
// LiveInterviewRecording row exists, and `playback_url` stays null until the
// session is "completed".
export interface LiveInterviewSummaryParticipant {
  role: string
  name?: string | null
  joined_at?: string | null
  left_at?: string | null
  connection_state: string
}

export interface LiveInterviewSummaryRecording {
  has_recording: boolean
  duration_seconds?: number | null
  playback_url?: string | null
}

// Same event_type vocabulary Proctored Interview's useIntegrityMonitor
// emits (multiple_persons, no_person, electronic_device, looking_away,
// camera_blocked, multiple_audio_sources) — see SummaryPage's own
// INTEGRITY_EVENT_LABELS. Empty for a human-mode session.
export interface LiveInterviewSummaryIntegrityEvent {
  event_type: string
  event_metadata?: Record<string, any> | null
  occurred_at?: string | null
}

// Phase D — one question's "current" (most-recently-scored) answer, as
// surfaced by both the summary and the live scoring-summary endpoints.
export interface LiveInterviewTestCaseResult {
  description?: string | null
  passed?: boolean | null
  explanation?: string | null
}

// One rubric criterion's score/feedback for a single answer — see
// LiveInterviewAnswer.rubric_criteria_scores.
export interface LiveInterviewRubricCriterionScore {
  criterion: string
  score: number
  feedback?: string | null
}

// One Run / Run Test Cases attempt, coding questions only — see
// LiveInterviewAnswer.run_history's own docstring for the exact shape.
export interface LiveInterviewRunHistoryEntry {
  action: 'run' | 'run_tests'
  at?: string | null
  code: string
  stdout?: string | null
  stderr?: string | null
  exit_code?: number | null
  results?: LiveInterviewTestCaseResult[]
}

export interface LiveInterviewSummaryQuestion {
  question_id: string
  sequence_number: number
  skill_name: string
  question_text: string
  status: string
  rubric_score?: number | null
  score_rationale?: string | null
  // The answer's own content, by modality. All optional because this same
  // shape is returned by BOTH the summary endpoint (which populates them)
  // and the live scoring-summary endpoint (which polls every few seconds and
  // deliberately leaves them unset — it has no use for a base64 whiteboard
  // image). Every one maps to a column that already exists on
  // live_interview_answers / live_interview_questions.
  modality?: string | null // audio | text | code | whiteboard
  duration_minutes?: number | null
  transcript?: string | null
  text_content?: string | null
  code_content?: string | null
  code_language?: string | null
  whiteboard_image_data?: string | null
  test_case_results?: LiveInterviewTestCaseResult[]
  // Per-criterion breakdown, absent (not an empty array) when this answer
  // wasn't scored per-criterion at all — see LiveInterviewAnswer
  // .rubric_criteria_scores.
  rubric_criteria_scores?: LiveInterviewRubricCriterionScore[] | null
  // Coding questions only — every Run / Run Test Cases attempt made while
  // working on this answer, oldest first. Empty for every non-coding answer.
  run_history?: LiveInterviewRunHistoryEntry[]
  // Short-lived playback URL for a spoken answer's recorded audio, minted
  // fresh by the server on every summary load and never stored. Unlike the
  // fields above it maps to no column at all — the server resolves it by
  // checking whether a blob exists at the path derived from the answer's id.
  // Absent whenever there is nothing to play: a typed answer, a browser that
  // could not record, or an upload that failed after the answer was saved.
  answer_audio_url?: string | null
}

// One skill's average score (0-100) across its scored questions. Computed
// server-side from the per-question scores — nothing per-skill is persisted.
export interface LiveInterviewSkillScore {
  skill_name: string
  score: number
}

export interface LiveInterviewSummary {
  session_id: string
  // Denormalized from the session so the summary page can navigate straight
  // back to this job's Live Interview tab.
  job_id: string
  status: string
  // "human" or "ai" — see RecruiterRoomPage's own AI-mode banner, shown when
  // this is "ai" (the whole interview is driven autonomously; the recruiter
  // room otherwise looks silently idle since it never listens to the AI
  // interviewer's own separate channel).
  interview_mode?: 'human' | 'ai'
  candidate_name?: string | null
  recruiter_seat_count: number
  started_at?: string | null
  ended_at?: string | null
  participants: LiveInterviewSummaryParticipant[]
  integrity_events?: LiveInterviewSummaryIntegrityEvent[]
  // Same shape as Proctored's scorecard: flag counts by event type
  // (informational entries left out), their total, and a short AI paragraph
  // ("No integrity concerns detected." when there are none; null if the AI
  // call failed).
  integrity_breakdown?: Record<string, number>
  integrity_total?: number
  integrity_summary?: string | null
  recording?: LiveInterviewSummaryRecording | null
  // Phase C — the recruiter room page's only source for the session's
  // current remaining question-type quota / ability estimate on mount;
  // updated thereafter from each generate-interview/next-question response.
  question_quota: Record<string, QuestionQuotaEntry[]>
  theta_estimate: number
  // Phase D, Part 5 — set once the interview is completed; both null before then.
  final_score?: number | null
  final_recommendation?: string | null
  // When the candidate's acknowledgement email (result + PDF scorecard) was
  // last sent — null until the first successful send. Loaded here so the
  // "Send/Resend Acknowledgement Mail" button reads correctly on page load,
  // not just right after a send in the same browser session.
  acknowledgement_sent_at?: string | null
  // Phase D — per-question answers/scores.
  questions: LiveInterviewSummaryQuestion[]
  // Average score per skill, derived from `questions` — skills with no
  // scored answer are absent rather than zero. Feeds the summary page's
  // skill radar.
  skill_scores: LiveInterviewSkillScore[]
  // The recruiter's main note (question_id null) plus any per-question notes
  // saved during the call — see LiveInterviewNote below.
  notes: LiveInterviewNote[]
  // Full persisted transcript + Timestamp-Linked Feedback Annotations —
  // rides along in this same call since transcript volume is bounded by
  // call duration (unlike chat, which is a separate GET).
  transcript_segments: LiveInterviewTranscriptSegment[]
  annotations: LiveInterviewAnnotation[]
  // The recruiter's Approve/Reject decision on this candidate — same shape
  // as Proctored Interview's own review fields. None until a decision has
  // been recorded.
  review_status?: 'approved' | 'rejected' | null
  review_reason?: string | null
  reviewed_by_name?: string | null
  reviewed_at?: string | null
  // How long the recruiter room should keep retrying its signaling WebSocket
  // after a drop before giving up. Server-configured
  // (LIVE_INTERVIEW_RECONNECT_TIMEOUT_SECONDS); this summary read is the
  // recruiter-side twin of the candidate's public session GET, which carries
  // the same value.
  reconnect_timeout_seconds: number
  // Server-configured (LIVE_TRANSCRIPTION_MAX_LIVE_SEGMENTS /
  // _REPORT_PAGE_SIZE) — see useLiveTranscription.ts and SummaryPage.tsx's
  // own usage for why these ride along here instead of being hardcoded.
  max_live_transcript_segments: number
  transcript_report_page_size: number
  candidate_feedback?: {
    rating: number
    tags?: string[]
    comment?: string | null
    submitted_at?: string | null
  } | null
  // Candidate's observed IP address plus a best-effort city/region/country
  // resolved from it server-side — same "IP-based location, not a substitute
  // for GPS" signal Proctored Interview's own scorecard shows. Both null
  // until the candidate has made at least one public request, or if the
  // location couldn't be resolved.
  candidate_ip_address?: string | null
  candidate_location?: string | null
}

export const getSessionSummary = async (sessionId: string): Promise<LiveInterviewSummary> => {
  const response = await apiV2.get(`/live-interview/sessions/${sessionId}/summary`)
  return response.data
}

// Records the recruiter's Approve/Reject decision, with a reason — required
// in both directions, same as Proctored Interview's own reviewSession.
// Returns the refreshed summary.
export const reviewSession = async (
  sessionId: string,
  status: 'approved' | 'rejected',
  reason: string,
): Promise<LiveInterviewSummary> => {
  const response = await apiV2.post(`/live-interview/sessions/${sessionId}/review`, { status, reason })
  return response.data
}

// "Jane_Doe_<kind>_2026-10-07_14-05.pdf" — candidate name, report kind, and
// the local date/time of the download.
const reportFileName = (candidateName: string | null | undefined, kind: string): string => {
  const name = (candidateName || 'Candidate').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'Candidate'
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${name}_${kind}_${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}-${pad(d.getMinutes())}.pdf`
}

// Same download-a-blob pattern as proctoredInterviewApi.ts's downloadScorecardPdf.
export const downloadLiveInterviewSummaryPdf = async (sessionId: string, candidateName?: string | null): Promise<void> => {
  const response = await apiV2.get(`/live-interview/sessions/${sessionId}/summary/pdf`, {
    responseType: 'blob',
  })
  const blob = response.data
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = reportFileName(candidateName, 'Live_Interview')
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}

export interface SendAcknowledgementResponse {
  sent: boolean
  message: string
  acknowledgement_sent_at?: string | null
}

// Emails the candidate their result (Selected/Rejected) with the same PDF
// scorecard downloadLiveInterviewSummaryPdf produces, attached. Rejected with
// a 422 server-side if final_recommendation is still "borderline" — the
// summary page disables the button in that case rather than relying solely
// on this to catch it.
export const sendLiveInterviewAcknowledgementMail = async (
  sessionId: string
): Promise<SendAcknowledgementResponse> => {
  const response = await apiV2.post(`/live-interview/sessions/${sessionId}/summary/send-acknowledgement`)
  return response.data
}

// Authenticated twin of liveInterviewPublicApi.postRecordingChunk — for the
// recruiter's browser, which runs the whole-meeting composited recorder in
// this app (see RecruiterRoomPage) and has no candidate access_token to call
// the public, token-gated endpoint with.
export const postRecordingChunk = (sessionId: string, index: number, chunk: Blob) =>
  apiV2
    .post(`/live-interview/sessions/${sessionId}/recording-chunks?index=${index}`, chunk, {
      headers: { 'Content-Type': 'application/octet-stream' },
    })
    .then((r) => r.data)

// ---------------------------------------------------------------------------
// Phase B: skills/rubric configuration + "generate only the first question"
// Mirrors api/v2/endpoints/live_interview.py's Phase B schemas exactly —
// same snake_case-everywhere convention as the rest of this file.
// ---------------------------------------------------------------------------

export interface RubricCriterion {
  name: string
  description?: string | null
}

export interface QuestionMixEntry {
  // A question_types.id (see /question-types) — plain string here, not a
  // UUID-branded type, matching the backend's LiveInterviewQuestion
  // .question_type_id String(64) column.
  question_type_id: string
  question_type_name?: string | null
  response_mode?: string | null
  category?: string | null
  count: number
}

export interface SkillRubric {
  skill_name: string
  weight: number
  rubric_criteria: RubricCriterion[]
  question_mix: QuestionMixEntry[]
}

export interface LiveInterviewConfig {
  id: string
  job_id: string
  version: number
  jd_context: Record<string, any>
  skills_rubrics: SkillRubric[]
  created_at: string
}

export interface LiveInterviewQuestion {
  id: string
  session_id: string
  question_type_id: string
  skill_name: string
  question_text: string
  guidance_notes?: string | null
  test_cases: Record<string, any>[]
  duration_minutes: number
  sequence_number: number
  status: string
  target_difficulty: number
  generated_at: string
  // Answer surface the candidate needs (code_editor / whiteboard / text /
  // verbal), resolved server-side from the question_types catalog.
  response_mode?: string | null
  category?: string | null
}

export interface GenerateInterviewResult {
  question: LiveInterviewQuestion
  question_quota: Record<string, any>
}

// Read-only hydrate: this job's existing config, or null when it has never
// been configured. Never creates a config and never re-runs the (slow, paid)
// JD extraction — that only happens when the recruiter explicitly asks via
// extractJdContext below. Mirrors proctoredInterviewApi.getConfigForJob.
export const getConfigForJob = async (jobId: string): Promise<LiveInterviewConfig | null> => {
  const response = await apiV2.get(`/live-interview/jobs/${jobId}/config`)
  return response.data ?? null
}

// Get-or-creates this job's LiveInterviewConfig and extracts JD context into
// it (role/seniority/summary/candidate_skills) via the backend's own
// standalone LLM call — never proctored interview's equivalent. Also seeds
// skills/rubrics from the suggested candidate_skills when none are
// configured yet (never overwriting skills the recruiter already edited).
export const extractJdContext = async (jobId: string): Promise<LiveInterviewConfig> => {
  const response = await apiV2.post(`/live-interview/jobs/${jobId}/config/extract-context`)
  return response.data
}

// Job-addressed save — get-or-creates the config server-side, so the editor
// can save without a config id (i.e. without having been forced through a
// JD extraction first).
export const saveSkillsRubricsForJob = async (
  jobId: string,
  skillsRubrics: SkillRubric[]
): Promise<LiveInterviewConfig> => {
  const response = await apiV2.put(`/live-interview/jobs/${jobId}/config/skills-rubrics`, {
    skills_rubrics: skillsRubrics,
  })
  return response.data
}

export const saveSkillsRubrics = async (
  configId: string,
  skillsRubrics: SkillRubric[]
): Promise<LiveInterviewConfig> => {
  const response = await apiV2.put(`/live-interview/configs/${configId}/skills-rubrics`, {
    skills_rubrics: skillsRubrics,
  })
  return response.data
}

// History (audit log) — who created/extracted-context/saved skills & rubrics
// on this config, and when. Mirrors proctoredInterviewApi's AuditLogEntry/
// getAuditLog exactly, with Live Interview's narrower action vocabulary
// (see LiveInterviewConfigAuditLog in backend/database/models.py — no
// question-bank regeneration or per-question edit actions here).
export interface AuditLogEntry {
  action: string
  version: number
  changed_by_name?: string | null
  details: Record<string, any>
  created_at: string
}

export const getAuditLog = async (configId: string): Promise<AuditLogEntry[]> => {
  const response = await apiV2.get(`/live-interview/configs/${configId}/audit-log`)
  return response.data
}

// Generates ONLY the first question for this session (later phases generate
// the rest one at a time against the returned question_quota).
export const generateInterview = async (sessionId: string): Promise<GenerateInterviewResult> => {
  const response = await apiV2.post(`/live-interview/sessions/${sessionId}/generate-interview`)
  return response.data
}

export const listQuestions = async (sessionId: string): Promise<LiveInterviewQuestion[]> => {
  const response = await apiV2.get(`/live-interview/sessions/${sessionId}/questions`)
  return response.data
}

// ---------------------------------------------------------------------------
// Phase C: answer capture + adaptive ("generate the next question") flow.
// Mirrors api/v2/endpoints/live_interview.py's Phase C schemas exactly.
// ---------------------------------------------------------------------------

// One entry of session_quota's nested-by-skill shape (see
// LiveInterviewSession.question_quota in database/models.py).
export interface QuestionQuotaEntry {
  question_type_id: string
  question_type_name?: string | null
  response_mode?: string | null
  category?: string | null
  remaining: number
}

export interface SubmitAnswerPayload {
  modality: 'audio' | 'text' | 'code' | 'whiteboard'
  transcript?: string | null
  text_content?: string | null
  code_content?: string | null
  code_language?: string | null
  whiteboard_image_data?: string | null
}

export interface LiveInterviewAnswerResult {
  id: string
  question_id: string
  modality: string
  rubric_score?: number | null
  score_rationale?: string | null
}

// Authenticated path — used for the recruiter's own "OK" button (always
// modality: 'audio', transcript: null — see RecruiterRoomPage.tsx's header
// comment: this codebase has no speech-to-text integration yet, a known,
// deliberate simplification for this pass, not a bug).
export const submitAnswer = async (
  questionId: string,
  payload: SubmitAnswerPayload
): Promise<LiveInterviewAnswerResult> => {
  const response = await apiV2.post(`/live-interview/questions/${questionId}/answer`, payload)
  return response.data
}

// Generates exactly one more question of `questionTypeId` UNDER `skillName`,
// targeted at the session's current ability estimate — returns the same
// {question, question_quota} shape generateInterview does.
//
// `skillName` is required — question_type_id is a shared, org-wide catalog
// id (e.g. "Coding") reused identically across every skill that includes it,
// so passing only the type id is ambiguous about WHICH skill's quota to
// consume (see the backend's generate_next_question docstring). Always pass
// the skill_name shown alongside the type in the dropdown/suggestion.
export const generateNextQuestion = async (
  sessionId: string,
  skillName: string,
  questionTypeId: string
): Promise<GenerateInterviewResult> => {
  const response = await apiV2.post(`/live-interview/sessions/${sessionId}/next-question`, null, {
    params: { skill_name: skillName, question_type_id: questionTypeId },
  })
  return response.data
}

// ---------------------------------------------------------------------------
// Phase D, Part 2: recruiter-only notes (one "main" note, question_id: null,
// plus at most one note per question). Recruiter-only by design — never
// exposed on liveInterviewPublicApi.ts.
// ---------------------------------------------------------------------------

export interface LiveInterviewNote {
  question_id?: string | null
  text: string
  updated_at?: string | null
}

export const getNotes = async (sessionId: string): Promise<LiveInterviewNote[]> => {
  const response = await apiV2.get(`/live-interview/sessions/${sessionId}/notes`)
  return response.data
}

export const saveNote = async (
  sessionId: string,
  questionId: string | null,
  text: string
): Promise<LiveInterviewNote> => {
  const response = await apiV2.put(`/live-interview/sessions/${sessionId}/notes`, {
    question_id: questionId,
    text,
  })
  return response.data
}

// ---------------------------------------------------------------------------
// Timestamp-Linked Feedback Annotations. Transcript LINES arrive live over
// the signaling WebSocket's `transcript-segment` app-message type (see
// hooks/useLiveTranscription.ts) — these REST calls only read the persisted
// transcript and manage annotations, added only from the Summary page.
// ---------------------------------------------------------------------------

export interface LiveInterviewTranscriptSegment {
  id: string
  participant_id: string
  role: 'recruiter' | 'candidate'
  speaker_name?: string | null
  start_seconds: number
  end_seconds: number
  text: string
  created_at?: string | null
}

export type AnnotationCategory = 'strength' | 'concern' | 'follow_up' | 'red_flag'

export interface LiveInterviewAnnotation {
  id: string
  transcript_segment_id?: string | null
  author_type: 'manual' | 'ai'
  author_user_id?: string | null
  author_name?: string | null
  category?: AnnotationCategory | null
  text: string
  created_at?: string | null
  updated_at?: string | null
}

export const getTranscript = async (sessionId: string): Promise<LiveInterviewTranscriptSegment[]> => {
  const response = await apiV2.get(`/live-interview/sessions/${sessionId}/transcript`)
  return response.data
}

export const getAnnotations = async (sessionId: string): Promise<LiveInterviewAnnotation[]> => {
  const response = await apiV2.get(`/live-interview/sessions/${sessionId}/annotations`)
  return response.data
}

export const createAnnotation = async (
  sessionId: string,
  transcriptSegmentId: string | null,
  category: AnnotationCategory | null,
  text: string
): Promise<LiveInterviewAnnotation> => {
  const response = await apiV2.post(`/live-interview/sessions/${sessionId}/annotations`, {
    transcript_segment_id: transcriptSegmentId,
    category,
    text,
  })
  return response.data
}

export const updateAnnotation = async (
  annotationId: string,
  updates: { text?: string; category?: AnnotationCategory | null }
): Promise<LiveInterviewAnnotation> => {
  const response = await apiV2.patch(`/live-interview/annotations/${annotationId}`, updates)
  return response.data
}

export const deleteAnnotation = async (annotationId: string): Promise<void> => {
  await apiV2.delete(`/live-interview/annotations/${annotationId}`)
}

// One-shot per session — the backend returns HTTP 409 if this session
// already has any AI-generated annotation (see transcript_annotations.
// generate_ai_annotations). Callers should disable the triggering button
// once `annotations.some(a => a.author_type === 'ai')` is already true,
// rather than relying on this call to fail as the only signal.
export const generateAiAnnotations = async (sessionId: string): Promise<LiveInterviewAnnotation[]> => {
  const response = await apiV2.post(`/live-interview/sessions/${sessionId}/annotations/generate`)
  return response.data
}

// ---------------------------------------------------------------------------
// Phase D, Part 3: in-call chat — history load + marking messages seen.
// Sending a message goes over the signaling WebSocket instead (see
// useLiveInterviewCall's sendAppMessage({ type: 'chat', text })) — these
// only ever read/ack.
// ---------------------------------------------------------------------------

export interface LiveInterviewChatMessage {
  id: string
  participant_id: string
  sender_role?: string | null
  sender_name?: string | null
  text: string
  sent_at?: string | null
  seen_by: string[]
}

export const getChatMessages = async (sessionId: string): Promise<LiveInterviewChatMessage[]> => {
  const response = await apiV2.get(`/live-interview/sessions/${sessionId}/chat`)
  return response.data
}

export const markChatSeen = async (sessionId: string, messageId: string): Promise<void> => {
  await apiV2.post(`/live-interview/sessions/${sessionId}/chat/${messageId}/seen`)
}

// ---------------------------------------------------------------------------
// Phase D, Part 4: live scoring summary — continue/stop heuristic + suggested
// next question type.
// ---------------------------------------------------------------------------

export interface LiveInterviewScoringSummary {
  session_id: string
  questions: LiveInterviewSummaryQuestion[]
  theta_estimate: number
  theta_standard_error: number
  recommendation: 'continue' | 'consider_stopping'
  suggested_next_question_type_id?: string | null
  // The specific skill the suggested type still has quota under — required
  // alongside suggested_next_question_type_id when calling generateNextQuestion,
  // since that type id alone is an org-wide catalog id shared across skills.
  suggested_skill_name?: string | null
  // Flat question_type_id -> remaining count. Enough for the suggest-next
  // heuristic, but it drops question_type_name/response_mode/category.
  remaining_quota: Record<string, number>
  // The session's LIVE question_quota, same nested-by-skill shape
  // getSessionSummary / generateInterview / generateNextQuestion return.
  // Present so the recruiter room can resync its quota panel + next-question
  // dropdown from this read (which runs on load, on a poll, and after every
  // answer) instead of only from its own write responses — see
  // RecruiterRoomPage's refreshScoring.
  question_quota: Record<string, QuestionQuotaEntry[]>
}

export const getScoringSummary = async (sessionId: string): Promise<LiveInterviewScoringSummary> => {
  const response = await apiV2.get(`/live-interview/sessions/${sessionId}/scoring-summary`)
  return response.data
}

// ---------------------------------------------------------------------------
// Phase D, Part 5: end interview + final score.
// ---------------------------------------------------------------------------

export interface CompleteSessionResult {
  session_id: string
  status: string
  ended_at?: string | null
  final_score?: number | null
  final_recommendation?: string | null
}

export const completeSession = async (sessionId: string): Promise<CompleteSessionResult> => {
  const response = await apiV2.post(`/live-interview/sessions/${sessionId}/complete`)
  return response.data
}

// ---------------------------------------------------------------------------
// AI interviewer — recruiter-side monitoring + control. Every AI response
// carries a JSON payload with start_interview / move_forward / end_interview
// flags; `decisions` lists those payloads, and controlAiInterviewer lets the
// recruiter trigger the same actions directly.
// ---------------------------------------------------------------------------

export type AiInterviewerControlAction = 'start' | 'move_forward' | 'end'

export interface AiInterviewerDecision {
  at: string
  phase: string
  // "ai" | "candidate_button" | "recruiter:<user id>"
  source: string
  flags: { start_interview: boolean; move_forward: boolean; end_interview: boolean }
  candidate_text?: string | null
}

export interface AiInterviewerState {
  session_id: string
  status: string
  phase: string
  decisions: AiInterviewerDecision[]
  pending_events: number
  available_actions: AiInterviewerControlAction[]
  recent_history: { role: string; text: string }[]
}

export const getAiInterviewerState = async (sessionId: string): Promise<AiInterviewerState> => {
  const response = await apiV2.get(`/live-interview/sessions/${sessionId}/ai-interviewer/state`)
  return response.data
}

export const controlAiInterviewer = async (sessionId: string, action: AiInterviewerControlAction): Promise<AiInterviewerState> => {
  const response = await apiV2.post(`/live-interview/sessions/${sessionId}/ai-interviewer/control`, { action })
  return response.data
}
